import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { catchError, Subject, Subscription, takeUntil, tap, throwError } from 'rxjs';
import { FeatureFlagUsecase } from 'src/app/application/feature-flags/feature-flag-use-case.service';
import { AuthDisplayerService } from 'src/app/application/usesCases/auth-displayer.service';
import { EventTrackingService } from 'src/app/application/usesCases/event-tracking.service';
import { FetchUserAttributesUsecase } from 'src/app/auth/application/usecases/fetch-user-attributes/fetch-user-attributes.usecase';
import { FeatureFlagEnum } from 'src/app/domain/features-flags/enum/featureFlag.enum';
import { Auth } from 'src/app/domain/models/auth.model';
import { AmplitudeEventsEnum } from 'src/app/infraestructure/ui/enums/amplitude';
import { AuthDataService } from 'src/app/infraestructure/ui/services/auth-data.service';
import { RoutingService } from 'src/app/infraestructure/ui/services/routing.service';
import { SpinnerHandlerService } from 'src/app/infraestructure/ui/services/spinner-handler.service';
import { ENVIRONMENT } from 'src/environments/environment';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent implements OnInit {
  @Output() showError = new EventEmitter<string>();

  userData: { name: string; password: string };
  userName: string = '';
  userPassword: string = '';
  disabledButton: boolean = true;
  pathAmplitude = ENVIRONMENT.urls.amplitude;

  showLoginForm!: boolean;

  private _fetchUserAttributesSubscription!: Subscription;
  /**
   * Subject to destroy the subscription.
   */
  private destroy$ = new Subject<void>();
  /**
   * variable to know if the feature flag is enabled
   */
  featureFlagShowOldLogin!: boolean;

  constructor(
    private authDisplayerService: AuthDisplayerService,
    private authDataService: AuthDataService,
    private eventTrackingService: EventTrackingService,
    private routingService: RoutingService,
    private spinnerHandlerService: SpinnerHandlerService,
    private fetchUserAttributesUsecase: FetchUserAttributesUsecase,
    private featureFlagService: FeatureFlagUsecase,
  ) {
    this.userData = {
      name: '',
      password: '',
    };
  }

  ngOnInit(): void {
    this.featureFlagService.configFeatureFlag(this.authDataService.getUserData()).subscribe({
      next: () => this.initData(),
      error: () => console.error('Error getting feature flag'),
    });
  }

  /**
   * @description Initializes the component.
   * @returns void
   */
  initData(): void {
    this.getValueFeatureFlag();
    this.showLoginForm = this.featureFlagShowOldLogin || ENVIRONMENT.login.showOldLogin;
    if (!this.showLoginForm) this.fetchUserAttributes();
  }

  onchangeNameField(event: Event): void {
    this.userData.name = (event as CustomEvent).detail;
    this.validateFields();
  }

  validateFields() {
    if (!!this.userData.name.length && !!this.userData.password.length) {
      this.disabledButton = false;
    } else {
      this.disabledButton = true;
    }
  }

  onchangePasswordField(event: Event): void {
    this.userData.password = (event as CustomEvent).detail;
    this.validateFields();
  }

  login(): void {
    this.authDisplayerService
      .askAuth(this.userData.name, this.userData.password)
      .subscribe({
        next: (data: Auth) => {
          this.successLogin(data);
          this.authDataService.sessionId = new Date().getTime();
          this.sendEventAmplitude();
        },
        error: (error: any) => {
          this.showErrorToast(error);
        },
      });
  }

  successLogin(data: Auth): void {
    this.authDataService.saveToken(data.token);
    this.authDataService.saveUserData(data);
    this.routingService.configInitialRoutes();
    this.spinnerHandlerService.handleRequest('plus');
  }

  showErrorToast(error: { error: string; message: string }): void {
    this.showError.emit(error.error);
  }

  sendEventAmplitude() {
    const userData = this.authDataService.getUserData();
    this.eventTrackingService.trackEvent(
      AmplitudeEventsEnum.COMERCIO_INICIO_SESION,
      { idRol: userData?.idRole || 0 }
    );
  }

  ngOnDestroy(): void {
    this.spinnerHandlerService.handleRequest('minus');
    this._fetchUserAttributesSubscription?.unsubscribe();
  }

  fetchUserAttributes(): void {
    this._fetchUserAttributesSubscription = this.fetchUserAttributesUsecase
      .execute()
      .pipe(
        tap((auth: Auth) => {
          this.successLogin(auth);
        }),
        catchError((error) => {
          window.open(ENVIRONMENT.login.url, '_self', 'noopener');
          return throwError(() => error);
        })
      )
      .subscribe();
  }

  /**
  * @description Retrieves the value of the feature flag `CREATEAUDIENCE` and sets the value of `featureFlagAudiencia`.
  */
  getValueFeatureFlag() {
    this.featureFlagService.getFeatureFlag(FeatureFlagEnum.GLOBAL_GLOBAL_SHOW_OLD_LOGIN)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: boolean) => {
        this.featureFlagShowOldLogin = res;
      });
  }
}
